import axios from "axios";
import { devWarn } from "./logger";

const BASE = process.env.REACT_APP_BACKEND_URL;

// Session handling:
//  - The session JWT now lives in an HttpOnly cookie set by the API, so page
//    scripts cannot read it. `withCredentials` makes the browser send it.
//  - `sm_csrf` is a readable cookie that must be echoed back in the
//    X-CSRF-Token header on every state-changing request (double submit).
//  - LEGACY: members signed in before the cookie migration still have a token in
//    localStorage. It is sent as a Bearer header until AuthContext has exchanged
//    it for cookies, then it is deleted. No new token is ever written there.
export const LEGACY_TOKEN_KEY = "sm_token";
const CSRF_COOKIE = "sm_csrf";
const UNSAFE_METHODS = new Set(["post", "put", "patch", "delete"]);

export const api = axios.create({ baseURL: `${BASE}/api`, withCredentials: true });

export function readCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

export function getLegacyToken() {
  try {
    return localStorage.getItem(LEGACY_TOKEN_KEY);
  } catch (err) {
    devWarn("api.legacyTokenRead", err);
    return null;
  }
}

export function clearLegacyToken() {
  try {
    localStorage.removeItem(LEGACY_TOKEN_KEY);
  } catch (err) {
    devWarn("api.legacyTokenClear", err);
  }
}

api.interceptors.request.use((config) => {
  const legacyToken = getLegacyToken();
  if (legacyToken) config.headers.Authorization = `Bearer ${legacyToken}`;
  if (UNSAFE_METHODS.has((config.method || "get").toLowerCase())) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) config.headers["X-CSRF-Token"] = csrf;
  }
  return config;
});

export function apiError(err, fallback = "Something went wrong. Please try again.") {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return err?.message === "Network Error" ? "Cannot reach the server right now." : fallback;
}

export const PROGRAM_LABELS = {
  AA: "Alcoholics Anonymous",
  NA: "Narcotics Anonymous",
  CMA: "Crystal Meth Anonymous",
  SMART: "SMART Recovery",
  MULTIPLE: "Multiple Programs",
  OTHER: "Other Recovery",
};

export const PROGRAM_SHORT = {
  AA: "AA",
  NA: "NA",
  CMA: "CMA",
  SMART: "SMART",
  MULTIPLE: "Multiple",
  OTHER: "Other",
};

export const PROGRAM_COLORS = {
  AA: "#2F6F6D",
  NA: "#2E5B8A",
  CMA: "#8A5A2E",
  SMART: "#6B5CA5",
  MULTIPLE: "#3B6B4A",
  OTHER: "#5B6B63",
};

export const AVAILABILITY = {
  accepting: { label: "Accepting Sponsees", color: "#1F7A4D", soft: "#E4F3EA", icon: "check" },
  limited: { label: "Limited Availability", color: "#B7791F", soft: "#FBF0DC", icon: "clock" },
  not_accepting: { label: "Not Accepting", color: "#B42318", soft: "#FBE6E4", icon: "pause" },
};

export const COMM_LABELS = {
  messaging: "Messaging",
  phone: "Phone",
  video: "Video",
  in_person: "In-person",
};

export const AVAILABLE_FOR_LABELS = {
  messaging: "Messaging",
  phone: "Phone",
  video: "Video",
  step_work: "Step work",
  general_support: "General recovery support",
};

export const GENDER_LABELS = {
  man: "Man",
  woman: "Woman",
  nonbinary: "Nonbinary",
  prefer_not_to_say: "Prefer not to say",
  no_preference: "No preference",
};

export const REPORT_REASON_LABELS = {
  harassment: "Harassment",
  sexual_behavior: "Sexual behavior",
  threats: "Threats",
  spam: "Spam",
  asking_for_money: "Asking for money",
  drug_sales: "Drug sales",
  dangerous_advice: "Dangerous advice",
  fake_sponsor: "Fake sponsor",
  other: "Other",
};

export function initials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");
}

export function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function formatDate(iso) {
  if (!iso) return "\u2014";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}
